import {
  PageHeader, SectionCard, Skeleton, SkeletonButton, SkeletonInput, SkeletonRegion,
  SkeletonStats, SkeletonTable, SkeletonText,
} from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { Building2, CreditCard, Lightbulb, QrCode, Receipt } from 'lucide-react';
import { STACK } from '../../constants/density';
import { useTranslation } from '../../i18n';
import useResponsive from '../../hooks/useResponsive';
import { MobileHeader, MobileScreen } from '../../mobile';

/**
 * «کلینیک من» while it is waiting — the page's own anatomy in placeholder
 * blocks, drawn for a `view` (DESIGN.md §"Loading & skeleton"). The router's
 * Suspense fallback imports this EAGERLY and the screen reuses the body
 * pieces for its own first load, so the two draw the same shape and nothing
 * jumps when the clinic record lands.
 *
 * Archetype D, zones ① ③ ④ ⑤ (Clinic.web.jsx): PageHeader with its sub-line,
 * the KPI strip, the view SegmentedControl, then either P1 stacked
 * SectionCards (profile, billing, whatsapp) or the P2 main + rail (connect).
 *
 * Section titles are static and render for real — the SectionCard is the
 * chrome the reader recognises the page by. The fields, figures, QR and rows
 * inside are blocks. Light by contract: kit, tokens, density, i18n, mobile
 * primitives, lucide — never the screen or its hooks.
 */

const VIEWS = new Set(['profile', 'connect', 'billing', 'whatsapp', 'inventory']);
export const resolveClinicSkeletonView = (view) => (VIEWS.has(view) ? view : 'profile');

/**
 * The kit's `SkeletonRegion` under this file's own prop names.
 *
 * It is the one announced `role="status"` when it is outermost and a plain
 * `aria-hidden` box when nested in another, so the whole-page skeleton — header
 * band plus a view body, each a region in its own right — announces "loading"
 * once rather than twice (DESIGN.md §"Loading & skeleton" — region root).
 */
function Status({ label, testId, className, style, children }) {
  return (
    <SkeletonRegion label={label} data-testid={testId} className={className} style={style}>
      {children}
    </SkeletonRegion>
  );
}

// ── Bodies, shared with the screens' own first load ─────────────────────────

/**
 * `ClinicProfileForm` + `ClinicStatusRow`: the identity card's field grid
 * (name, type, province, city, address across both columns, phone) and the
 * active-toggle row. One column on the phone, two on the desk.
 */
export function ClinicProfileSkeleton({ compact = false }) {
  const { t } = useTranslation();
  return (
    <Status label={t('common.state.loading')} testId="clinic-profile-skeleton" className={compact ? 'flex flex-col gap-4' : 'max-w-[1100px] space-y-5'}>
      <SectionCard title={t('clinic.sections.identity')} icon={Building2}>
        <div className={compact ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
          <SkeletonInput />
          <SkeletonInput />
          <SkeletonInput />
          <SkeletonInput />
          <SkeletonInput style={compact ? undefined : { gridColumn: '1 / -1' }} />
          <SkeletonInput />
        </div>
      </SectionCard>
      <SectionCard title={t('clinic.sections.status')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Skeleton width="55%" height={14} />
          <Skeleton width={44} height={24} radius={RADIUS.pill} />
        </div>
      </SectionCard>
    </Status>
  );
}

/** `QrPanel` (the code square, two copy fields, three buttons) beside `ConnectGuide`. */
export function ClinicConnectSkeleton({ compact = false }) {
  const { t } = useTranslation();
  const qr = compact ? 180 : 200;
  return (
    <Status
      label={t('common.state.loading')}
      testId="clinic-connect-skeleton"
      className={compact ? 'flex flex-col gap-4' : 'grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_340px]'}
    >
      <SectionCard title={t('clinic.sections.qr')} icon={QrCode}>
        <div className="flex justify-center">
          <div className="inline-flex rounded-2xl border-2 border-border bg-white p-5 shadow-card">
            <Skeleton width={qr} height={qr} radius={8} />
          </div>
        </div>
        <SkeletonInput />
        <SkeletonInput />
        <div className="flex flex-wrap gap-2">
          <SkeletonButton width={132} />
          <SkeletonButton width={112} />
          <SkeletonButton width={96} />
        </div>
      </SectionCard>
      <SectionCard title={t('clinic.sections.guide')} icon={Lightbulb}>
        <SkeletonText lines={4} height={12} gap={12} />
        <div className="border-t border-border pt-4">
          <Skeleton width="70%" height={12} />
        </div>
      </SectionCard>
    </Status>
  );
}

/** One offer card (سالانه or دائمی): heading, struck price, price, saving box, action. */
function OfferCardSkeleton({ compact }) {
  return (
    <div
      aria-hidden
      style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: RADIUS.card,
        padding: compact ? 16 : 20, display: 'flex', flexDirection: 'column', gap: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Skeleton width={44} height={44} radius={12} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
          <Skeleton width="45%" height={15} />
          <Skeleton width="70%" height={11} />
        </div>
      </div>
      <Skeleton width="50%" height={14} />
      <Skeleton width="65%" height={compact ? 30 : 38} />
      <Skeleton width="100%" height={compact ? 64 : 72} radius={12} />
      <SkeletonButton width="100%" />
    </div>
  );
}

/**
 * `BillingView`'s body: the flat status hero (phone only), the plans card with
 * its two offers — سالانه and دائمی, side by side on a desktop and stacked on a
 * phone — the period history and the reminders row.
 */
export function BillingBodySkeleton({ compact = false, children }) {
  const { t } = useTranslation();
  return (
    <Status label={t('common.state.loading')} testId="subscription-loading" className={compact ? 'space-y-4' : 'space-y-5'}>
      {children}
      {compact ? (
        <div className="rounded-2xl border border-border bg-white p-4 shadow-card" aria-hidden>
          <div className="flex items-start justify-between gap-3">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
              <Skeleton width="40%" height={14} />
              <Skeleton width="80%" height={12} />
              <Skeleton width="55%" height={12} />
            </div>
            <Skeleton width={72} height={24} radius={RADIUS.pill} />
          </div>
        </div>
      ) : null}
      <SectionCard title={t('subscription.sections.plans')} icon={CreditCard}>
        <div
          data-testid="plan-offers-skeleton"
          className={compact ? 'flex flex-col gap-5' : 'flex flex-wrap gap-5'}
        >
          {[0, 1].map((i) => (
            <div key={i} className={compact ? '' : 'min-w-0 flex-[1_1_360px]'}>
              <OfferCardSkeleton compact={compact} />
            </div>
          ))}
        </div>
      </SectionCard>
      <SectionCard title={t('subscription.sections.history')} icon={Receipt}>
        {compact ? (
          <div className="flex flex-col gap-2" aria-hidden>
            {[0, 1].map((i) => (
              <div key={i} className="rounded-lg border border-border bg-white p-3">
                <div className="flex items-center justify-between gap-2">
                  <Skeleton width="45%" height={13} />
                  <Skeleton width={64} height={13} />
                </div>
                <Skeleton width="60%" height={11} style={{ marginTop: 8 }} />
              </div>
            ))}
          </div>
        ) : (
          <SkeletonTable rows={3} columns={4} label={t('common.state.loading')} />
        )}
      </SectionCard>
      <SectionCard title={t('subscription.sections.reminders')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Skeleton width="50%" height={14} />
          <SkeletonButton width={140} />
        </div>
      </SectionCard>
    </Status>
  );
}

/** `WhatsappView`'s body: the state panel, the warning band, the link panel. */
export function WhatsappBodySkeleton({ compact = false }) {
  const { t } = useTranslation();
  const pad = compact ? 14 : 20;
  return (
    <Status label={t('common.state.loading')} testId="whatsapp-loading">
      <div aria-hidden style={{ display: 'flex', alignItems: 'center', gap: 14, padding: compact ? '14px' : '16px 18px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 14 }}>
        <Skeleton width={42} height={42} radius={12} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Skeleton width="35%" height={14} />
          <Skeleton width="70%" height={12} />
        </div>
      </div>
      <Skeleton width="100%" height={52} radius={RADIUS.card} style={{ marginTop: 16 }} />
      <div aria-hidden style={{ marginTop: 20, padding: pad, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 14 }}>
        <Skeleton width="100%" height={52} radius={RADIUS.card} style={{ marginBottom: 18 }} />
        <SkeletonButton width={compact ? '100%' : 168} />
        <Skeleton width="60%" height={11} style={{ marginTop: 10 }} />
      </div>
    </Status>
  );
}

/**
 * «انبار» — the three depth cards, at the size `InventoryTierView` draws them.
 *
 * Three equal columns on the desk and one on the phone, each card 14px of
 * padding around a 14px title and a two-line description, inside the 2px
 * border the active one wears — so the card the reader is about to choose
 * does not move when the settings land.
 */
export function InventoryTierBodySkeleton({ compact = false }) {
  const { t } = useTranslation();
  return (
    <Status
      label={t('common.state.loading')}
      testId="clinic-inventory-skeleton"
      className={compact ? 'flex flex-col gap-4' : 'max-w-[1100px] space-y-5'}
    >
      <div
        aria-hidden
        style={{
          display: 'grid',
          gridTemplateColumns: compact ? '1fr' : 'repeat(3, 1fr)',
          gap: 12,
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              padding: 14, borderRadius: 12, background: '#fff',
              border: `2px solid ${C.border}`,
            }}
          >
            <Skeleton width={88 + i * 12} height={14} style={{ marginBottom: 8 }} />
            <Skeleton width="100%" height={12} style={{ marginBottom: 6 }} />
            <Skeleton width="72%" height={12} />
          </div>
        ))}
      </div>
      {/* The fine switches only exist at «پیشرفته», so the placeholder shows the
          one row that is always there rather than promising a list. */}
      <div
        aria-hidden
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, padding: '12px 14px', background: '#fff',
          border: `1px solid ${C.border}`, borderRadius: 12,
        }}
      >
        <Skeleton width="42%" height={13} />
        <Skeleton width={40} height={22} radius={RADIUS.pill} />
      </div>
    </Status>
  );
}

function Body({ view, compact }) {
  if (view === 'billing') return <BillingBodySkeleton compact={compact} />;
  if (view === 'whatsapp') return <WhatsappBodySkeleton compact={compact} />;
  if (view === 'connect') return <ClinicConnectSkeleton compact={compact} />;
  if (view === 'inventory') return <InventoryTierBodySkeleton compact={compact} />;
  return <ClinicProfileSkeleton compact={compact} />;
}

/** The ⑤ zone alone, for a screen whose header, KPIs and toolbar are already real. */
export function ClinicViewSkeleton({ view: requested = 'profile', compact = false }) {
  return <Body view={resolveClinicSkeletonView(requested)} compact={compact} />;
}

/** `BillingView`'s pending body — the name the registry side uses. */
export const BillingViewSkeleton = BillingBodySkeleton;

// ── The pages ────────────────────────────────────────────────────────────────

export function ClinicSkeletonWeb({ view: requested = 'profile' }) {
  const { t } = useTranslation();
  const view = resolveClinicSkeletonView(requested);
  const label = t('common.state.loading');
  return (
    <SkeletonRegion
      className="flex h-full flex-col bg-background"
      label={label}
      data-testid="clinic-skeleton"
      data-view={view}>
      <PageHeader title={t('nav.items.clinic')} sub={<Skeleton width={220} height={12} />} />
      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        {/* ③ the four KPI cards, each with an icon well */}
        <div className="mb-5">
          <SkeletonStats count={4} label={label} />
        </div>
        {/* ④ the three-view control */}
        <div className="mb-5" aria-hidden>
          <div style={{ display: 'inline-flex', gap: 4, padding: 4, borderRadius: 12, background: C.bg }}>
            {[88, 96, 104].map((w, i) => <Skeleton key={i} width={w} height={36} radius={9} />)}
          </div>
        </div>
        {/* ⑤ */}
        <div className={view === 'whatsapp' ? 'max-w-[1100px]' : undefined}>
          {view === 'whatsapp' ? (
            <div style={{ maxWidth: 640 }}>
              <div style={{ marginBottom: 24 }}>
                <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 800, color: C.textPrimary }}>{t('settings.whatsapp.title')}</h2>
                <Skeleton width="60%" height={12} style={{ marginTop: 8 }} />
              </div>
              <WhatsappBodySkeleton />
            </div>
          ) : <Body view={view} compact={false} />}
        </div>
      </div>
    </SkeletonRegion>
  );
}

export function ClinicSkeletonMobile({ view: requested = 'profile' }) {
  const { t } = useTranslation();
  const view = resolveClinicSkeletonView(requested);
  const label = t('common.state.loading');
  return (
    <SkeletonRegion
      className="flex h-full flex-col bg-background"
      label={label}
      data-testid="clinic-skeleton"
      data-view={view}>
      <MobileScreen
        header={<MobileHeader title={t('nav.items.clinic')} subtitle={<Skeleton tone="onDark" width={160} height={11} />} />}
      >
        {/* the section strip: three tab labels over a rule */}
        <div aria-hidden style={{ display: 'flex', gap: 4, padding: '0 0.25rem', borderBottom: `1px solid ${C.border}`, background: C.bg }}>
          {[64, 88, 72].map((w, i) => (
            <div key={i} style={{ padding: '12px 14px' }}><Skeleton width={w} height={12} /></div>
          ))}
        </div>
        <div className="flex flex-col gap-4 p-4" style={{ gap: STACK.regular + 4 }}>
          {view === 'whatsapp' ? (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: C.textPrimary }}>{t('settings.whatsapp.title')}</h2>
                <Skeleton width="70%" height={12} style={{ marginTop: 8 }} />
              </div>
              <WhatsappBodySkeleton compact />
            </div>
          ) : <Body view={view} compact />}
        </div>
      </MobileScreen>
    </SkeletonRegion>
  );
}

export default function ClinicSkeleton(props) {
  const { isMobile } = useResponsive();
  return isMobile ? <ClinicSkeletonMobile {...props} /> : <ClinicSkeletonWeb {...props} />;
}
